import { parseBackendTimestamp } from '../../pages/lanes/aging';

type When = string | Date | null | undefined;

const toDate = (value: When): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : parseBackendTimestamp(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const pad = (n: number) => String(n).padStart(2, '0');

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** 10:14 */
export const formatClock = (value: When): string => {
  const date = toDate(value);
  return date ? `${pad(date.getHours())}:${pad(date.getMinutes())}` : '';
};

/** 28/09/2026 */
export const formatDate = (value: When): string => {
  const date = toDate(value);
  return date ? `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}` : 'Unknown';
};

/** 28/09/2026, 10:14 (the app's usual format). */
export const formatWhen = (value: When): string => {
  const date = toDate(value);
  return date ? `${formatDate(date)}, ${formatClock(date)}` : 'Unknown';
};

/** Today, 10:14 / Yesterday, 17:12 / 27/09/2026, 10:05 */
export const formatDayTime = (value: When, now: Date = new Date()): string => {
  const date = toDate(value);
  if (!date) return 'Unknown';
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(date, now)) return `Today, ${formatClock(date)}`;
  if (sameDay(date, yesterday)) return `Yesterday, ${formatClock(date)}`;
  return formatWhen(date);
};

/** Just now / 3 hours ago / Yesterday / 5 days ago / 01/08/2026 */
export const formatAgo = (value: When, now: Date = new Date()): string => {
  const date = toDate(value);
  if (!date) return 'Unknown';
  const seconds = Math.max(0, (now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 14) return `${days} days ago`;
  return formatDate(date);
};

export const formatCount = (n: number): string => n.toLocaleString('en-GB');

export const formatSize = (bytes: number | null | undefined): string => {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const fileKind = (contentType: string | null | undefined, name: string | null | undefined): string => {
  const type = (contentType || '').toLowerCase();
  const extension = (name || '').toLowerCase().split('.').pop() || '';
  if (type.includes('pdf') || extension === 'pdf') return 'PDF';
  if (type.includes('word') || extension === 'doc' || extension === 'docx') return 'Word';
  if (type.includes('sheet') || type.includes('excel') || extension === 'xls' || extension === 'xlsx' || extension === 'csv') return 'Excel';
  if (type.includes('zip') || extension === 'zip') return 'ZIP';
  if (type.startsWith('image/')) return 'Image';
  return 'File';
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 2026-09-28 -> 28 Sep */
export const shortDay = (isoDay: string): string => {
  const [year, month, day] = isoDay.split('-').map(Number);
  if (!year || !month || !day || month > 12) return isoDay;
  return `${day} ${MONTHS[month - 1]}`;
};

export const percent = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0);

/** '2026-11-06' -> 06/11/2026, read by hand (a date-only string never goes through new Date). */
export const formatIsoDay = (value: string | null | undefined): string => {
  const match = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  return match ? `${match[3]}/${match[2]}/${match[1]}` : 'Unknown';
};
